import type { CogoDataSource } from "@cogo/types"

export class DataSourceManager {
  private sources: Record<string, CogoDataSource>
  private cache = new Map<string, unknown>()
  private basePath: string

  constructor(sources: Record<string, CogoDataSource>, basePath = "") {
    this.sources = sources
    this.basePath = basePath
  }

  async load(name: string): Promise<unknown> {
    if (this.cache.has(name)) return this.cache.get(name)

    const source = this.sources[name]
    if (!source) throw new Error(`DataSource not found: ${name}`)

    let data: unknown

    if (source.type === "static") {
      if (source.data != null) {
        data = structuredClone(source.data)
      } else if (source.path) {
        const rawPath = source.path
        const url = rawPath.startsWith("http")
          ? rawPath
          : rawPath.startsWith("/")
            ? `${this.basePath}${rawPath}`
            : `${this.basePath}/${rawPath}`
        const res = await fetch(url)
        if (!res.ok) throw new Error(`Failed to load: ${url}`)
        data = await res.json()
      } else {
        data = null
      }
    } else if (source.type === "rest") {
      const res = await fetch(source.endpoint!, {
        method: source.method ?? "GET",
        headers: source.headers,
      })
      if (!res.ok) throw new Error(`API error: ${source.endpoint}`)
      const json = await res.json()
      data = source.responseMapping
        ? this.extractPath(json, source.responseMapping)
        : json
    } else if (source.type === "gemini") {
      const model = source.model ?? "gemini-2.5-pro"
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`,
        {
          method: "POST",
          headers: {
            "x-goog-api-key": source.apiKey ?? "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contents: [{ role: "user", parts: [{ text: source.prompt ?? "" }] }],
          }),
        },
      )
      if (!res.ok) throw new Error(`Gemini API error: ${res.status}`)
      const json = await res.json() as Record<string, unknown>
      if (source.responseMapping) {
        data = this.extractPath(json, source.responseMapping)
      } else {
        // Default: extract text from first candidate
        const candidates = json.candidates as Array<Record<string, unknown>> | undefined
        const firstCandidate = candidates?.[0]
        const content = firstCandidate?.content as Record<string, unknown> | undefined
        const parts = content?.parts as Array<Record<string, unknown>> | undefined
        data = parts?.[0]?.text ?? json
      }
    }

    this.cache.set(name, data)
    return data
  }

  preload(name: string, data: unknown): void {
    this.cache.set(name, data)
  }

  invalidate(name: string): void {
    this.cache.delete(name)
  }

  invalidateAll(): void {
    this.cache.clear()
  }

  /** Register a datasource definition (block-level sources). Does not override existing. */
  registerSource(name: string, source: CogoDataSource): void {
    if (!(name in this.sources)) {
      this.sources[name] = source
    }
  }

  private extractPath(obj: unknown, path: string): unknown {
    return path.split(".").reduce(
      (acc, key) => (acc as Record<string, unknown>)?.[key],
      obj,
    )
  }
}
